import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navigationItems = [
    {
      name: 'Company',
      items: [
        { name: 'About UE', path: '/about', description: 'Our vision and mission' },
        { name: 'Corporate Structure', path: '/corporate-structure', description: 'Entities and governance' },
        { name: 'Leadership', path: '/corporate-structure#leadership', description: 'Executive team and board' },
        { name: 'Careers', path: '/careers', description: 'Join our team' },
      ]
    },
    {
      name: 'Technology',
      path: '/technology',
      items: [
        { name: 'Platform Overview', path: '/technology', description: 'Our marketplace infrastructure' },
        { name: 'Technical Stack', path: '/technology#stack', description: 'Core technologies' },
        { name: 'API & Integrations', path: '/technology#api', description: 'Developer resources' },
        { name: 'Security', path: '/compliance#security', description: 'Data protection and privacy' },
      ]
    },
    {
      name: 'Impact',
      path: '/impact',
      items: [
        { name: 'Economic Development', path: '/impact', description: 'Formalizing micro-businesses' },
        { name: 'ESG Metrics', path: '/impact#esg', description: 'Environmental and social impact' },
        { name: 'Case Studies', path: '/impact#case-studies', description: 'Success stories' },
      ]
    },
    {
      name: 'Investors',
      path: '/investor-relations',
    },
    {
      name: 'Partners',
      path: '/partnerships',
    },
  ];

  const secondaryItems = [
    { name: 'Press', path: '/press' },
    { name: 'Compliance', path: '/compliance' },
  ];

  const isActivePath = (path) => {
    if (!path) return false;
    const basePath = path.split('#')[0];
    return location?.pathname === basePath;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleDropdownEnter = (name) => {
    setActiveDropdown(name);
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-header transition-all duration-200 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-corporate shadow-corporate-sm'
          : 'bg-white'
      }`}
    >
      {/* Top Bar - Secondary Navigation */}
      <div className="border-b border-border-light">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-end h-8 text-xs">
            {secondaryItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-1 transition-colors duration-150 ${
                  isActivePath(item.path)
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-wide mx-auto px-4 lg:px-8" ref={dropdownRef}>
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
          >
            <div className="flex items-center justify-center w-9 h-9 bg-primary rounded-lg">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-white"
              >
                <path
                  d="M12 2L2 7L12 12L22 7L12 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  fill="currentColor"
                  fillOpacity="0.2"
                />
                <path
                  d="M2 17L12 22L22 17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 12L12 17L22 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="text-xl font-semibold text-foreground tracking-tight">
              United Empresas
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.items && handleDropdownEnter(item.name)}
                onMouseLeave={handleDropdownLeave}
              >
                {item.items ? (
                  <button
                    className={`flex items-center space-x-1 px-4 py-2 text-sm font-medium transition-colors duration-150 rounded-md ${
                      activeDropdown === item.name || (item.path && isActivePath(item.path))
                        ? 'text-foreground bg-muted'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <span>{item.name}</span>
                    <Icon
                      name="ChevronDown"
                      size={14}
                      className={`transition-transform duration-200 ${
                        activeDropdown === item.name ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={`px-4 py-2 text-sm font-medium transition-colors duration-150 rounded-md block ${
                      isActivePath(item.path)
                        ? 'text-foreground bg-muted'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}

                {/* Dropdown Menu */}
                {item.items && activeDropdown === item.name && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-border rounded-brand shadow-corporate-md animate-slide-down">
                    <div className="p-2">
                      {item.items.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`block px-4 py-3 rounded-brand-sm transition-colors duration-150 ${
                            isActivePath(subItem.path)
                              ? 'bg-muted'
                              : 'hover:bg-muted'
                          }`}
                        >
                          <div className="text-sm font-medium text-foreground">
                            {subItem.name}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {subItem.description}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link
              to="/investor-relations"
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-150"
            >
              Investor Relations
            </Link>
            <Link
              to="/partnerships"
              className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-light rounded-brand-sm transition-colors duration-150"
            >
              Partner With Us
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-brand-sm hover:bg-muted transition-colors duration-150"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? "X" : "Menu"}
              size={24}
              className="text-foreground"
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-white animate-slide-down">
          <div className="max-w-wide mx-auto px-4 py-4">
            <nav className="space-y-1">
              {navigationItems.map((item) => (
                <div key={item.name}>
                  {item.items ? (
                    <div className="space-y-1">
                      <div className="px-4 py-2 text-sm font-semibold text-foreground">
                        {item.name}
                      </div>
                      {item.items.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`block px-4 py-2 pl-8 text-sm transition-colors duration-150 rounded-brand-sm ${
                            isActivePath(subItem.path)
                              ? 'text-foreground bg-muted'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`block px-4 py-2 text-sm font-medium transition-colors duration-150 rounded-brand-sm ${
                        isActivePath(item.path)
                          ? 'text-foreground bg-muted'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}

              <div className="pt-4 mt-4 border-t border-border space-y-2">
                {secondaryItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              <div className="pt-4 space-y-2">
                <Link
                  to="/investor-relations"
                  className="block px-4 py-3 text-center text-sm font-medium text-foreground border border-border rounded-brand-sm hover:bg-muted transition-colors duration-150"
                >
                  Investor Relations
                </Link>
                <Link
                  to="/partnerships"
                  className="block px-4 py-3 text-center text-sm font-medium text-white bg-primary rounded-brand-sm hover:bg-primary-light transition-colors duration-150"
                >
                  Partner With Us
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
